import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import { initialDockWidth } from "../geometry/dock-default-width"
import { Types as HistoryTypes } from "../../../../actions/app/history"

/** `full` promotes the same dock node to cover the stage; it is a view state, never a route (ADR 0020). */
export type DockView = "closed" | "docked" | "full"

/** Which body the chat surface shows. The conversations panel stays either way — this is not a route. */
export type ChatSurfaceView = "conversation" | "artifacts"

export type DockPane = "chat" | "skills"

export interface ComposerSeed {
  text: string
  /** Epoch ms at dispatch; lets the consumer reject seeds stranded by a navigation that never mounted it. */
  seededAt: number
}

export interface AgentChatState {
  /** Whether the Kelly AI conversations side panel is collapsed to the top-bar "Chats" dock affordance. */
  panelCollapsed: boolean
  /** Prompt to pre-fill the Kelly composer with; set by the nav nudge, consumed once by KellyChatProvider. */
  pendingComposerSeed: ComposerSeed | null
  /**
   * Sticky once Kelly has been shown in this account session. Gates two things that now flip on the same
   * event: the hoisted controller's thread-list query (ADR 0018) and the dock's transcript surface, which
   * mounts on first show and never unmounts. An account that never opens Kelly pays no `ChatBox` per page.
   */
  hasActivated: boolean
  /** Which view the single app-level dock is in (ADR 0019, ADR 0020). */
  dockView: DockView
  dockPane: DockPane
  /** The user's committed dock width. Nothing re-clamps it: `dockWidthCss` re-resolves the bounds at paint time. */
  dockWidthPx: number
  /**
   * Conversation transcript or the Artifacts list. In the store rather than in `ConversationSurface`
   * because the dock's conversations flyout renders the nav item as a sibling of the body it switches.
   */
  surfaceView: ChatSurfaceView
  /**
   * The resource id from a shared artifact's URL, while that route is mounted. Published by the route
   * bridge rather than read with `useMatch` where it is used — the artifact panel has a dozen hosts with
   * no Router. Same shape as `pendingComposerSeed`.
   */
  pendingArtifactDeepLink: string | null
}

export const AGENT_CHAT_INITIAL_STATE: AgentChatState = {
  panelCollapsed: false,
  pendingComposerSeed: null,
  hasActivated: false,
  dockView: "closed",
  dockPane: "chat",
  dockWidthPx: initialDockWidth(),
  surfaceView: "conversation",
  pendingArtifactDeepLink: null,
}

export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState: AGENT_CHAT_INITIAL_STATE,
  reducers: {
    collapsePanel(state) {
      state.panelCollapsed = true
    },
    expandPanel(state) {
      state.panelCollapsed = false
    },
    seedComposer: {
      reducer(state, action: PayloadAction<ComposerSeed>) {
        state.pendingComposerSeed = action.payload
      },
      prepare(text: string) {
        return { payload: { text, seededAt: Date.now() } }
      },
    },
    consumeComposerSeed(state) {
      state.pendingComposerSeed = null
    },
    /**
     * The only path that changes which view the dock is in — every control, launcher and the `/kelly-ai`
     * bridge goes through it, so there is one place where showing Kelly commits the sticky activation.
     * Activation stays sticky rather than tracking visibility: an un-sticky predicate would disable the
     * thread-list query on close, and React Query would refetch — and re-run the draft prune — on reopen.
     */
    setDockView(state, action: PayloadAction<DockView>) {
      state.dockView = action.payload
      if (action.payload !== "closed") state.hasActivated = true
      else state.dockPane = "chat"
    },
    openSkillsPane(state) {
      state.dockPane = "skills"
    },
    closeSkillsPane(state) {
      state.dockPane = "chat"
    },
    setDockWidth(state, action: PayloadAction<number>) {
      state.dockWidthPx = action.payload
    },
    setSurfaceView(state, action: PayloadAction<ChatSurfaceView>) {
      state.surfaceView = action.payload
    },
    setArtifactDeepLink(state, action: PayloadAction<string | null>) {
      state.pendingArtifactDeepLink = action.payload
    },
  },
  extraReducers(builder) {
    // Collapse is a per-session layout pref that persists across routes but must not leak across accounts —
    // reset it on account switch so a new account starts expanded (tabs are already route-scoped; ADR 0008).
    builder.addCase(HistoryTypes.SELECT_ACCOUNT, () => AGENT_CHAT_INITIAL_STATE)
  },
})

export const { collapsePanel, expandPanel, seedComposer, consumeComposerSeed, setDockView, setDockWidth, setSurfaceView, setArtifactDeepLink, openSkillsPane, closeSkillsPane } = agentChatSlice.actions

export const selectPanelCollapsed = (state: ReduxState): boolean => state.agentChat.panelCollapsed

export const selectPendingComposerSeed = (state: ReduxState): ComposerSeed | null => state.agentChat.pendingComposerSeed

export const selectHasActivated = (state: ReduxState): boolean => state.agentChat.hasActivated

export const selectDockView = (state: ReduxState): DockView => state.agentChat.dockView

export const selectDockPane = (state: ReduxState): DockPane => state.agentChat.dockPane

export const selectDockWidthPx = (state: ReduxState): number => state.agentChat.dockWidthPx

export const selectSurfaceView = (state: ReduxState): ChatSurfaceView => state.agentChat.surfaceView

export const selectArtifactDeepLink = (state: ReduxState): string | null => state.agentChat.pendingArtifactDeepLink

export default agentChatSlice.reducer
